import React, { useState, useEffect, useCallback } from "react";
import axiosInstance from "@/config/axiosInstance";
import { endpoints } from "@/config/apiEndpoints";
import { useTimezone } from '@/contexts/TimezoneContext';
import { detectUserTimezone, normalizeTimezone } from "@/utils/timezoneDetection";
import { Sidebar } from "@/components/Sidebar";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/Header";
import { Skeleton } from "@/components/ui/skeleton";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import Chatbot from "@/components/Chatbot";
import CallingBot from "@/components/Callingbot";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Search,
  Eye,
  Edit,
  Send,
  Users,
  TrendingUp,
  Share2,
  Calendar,
  Clock,
  Repeat,
  X,
  Loader2,
  CrossIcon,
  Trash,
  Circle,
  XCircle 
} from "lucide-react";
import {
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  isWithinInterval,
  isToday,
  subWeeks,
  subMonths,
} from "date-fns";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
// import CallingBot from "@/components/Callingbot";
// import Chatbot from "@/components/Chatbot";
import BotLauncher from "@/components/BotLauncher";
const CampaignManagement = () => {
  const [campaigns, setCampaigns] = useState<any[]>([]);
  const [recentCampaigns, setRecentCampaigns] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [dateRangeFilter, setDateRangeFilter] = useState("all");
  const navigate = useNavigate();
  const { timezone, setTimezone } = useTimezone();
  const [mobileMenuToggle, setMobileMenuToggle] = useState<(() => void) | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedCampaignDetails, setSelectedCampaignDetails] = useState<any>(null);
  const [campaignToCancel, setCampaignToCancel] = useState<any>(null);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [cancelStep, setCancelStep] = useState<"review" | "confirm">("review");
  // ── helpers ──────────────────────────────────────────────────

  const formatDateTimeInTimezone = (value?: string | null, tz?: string) => {
    if (!value) return "";
    const targetTz = tz || timezone || "UTC";
    try {
      return new Intl.DateTimeFormat(undefined, {
        timeZone: targetTz,
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
      }).format(new Date(value));
    } catch {
      return value;
    }
  };

  /** Compact version for mobile cards */
  const formatDateShort = (value?: string | null, tz?: string) => {
    if (!value) return "No date";
    const targetTz = tz || timezone || "UTC";
    try {
      return new Intl.DateTimeFormat(undefined, {
        timeZone: targetTz,
        month: "short",
        day: "numeric",
        year: "numeric",
      }).format(new Date(value));
    } catch {
      return value;
    }
  };

  const location = useLocation();

  const getCampaignDate = (campaign: any) => campaign.scheduled_date || campaign.date || null;

  const normalizeDateKey = (value?: string | null) => {
    if (!value) return "";
    const time = new Date(value).getTime();
    return Number.isNaN(time) ? String(value) : new Date(time).toISOString();
  };

  const isRecurringCampaign = (campaign: any): boolean =>
    campaign?.is_recurring === true || Boolean(campaign?.recurrence_type);

  const getRecurringDates = (campaign: any): string[] => {
    const dates = Array.isArray(campaign.scheduled_dates)
      ? campaign.scheduled_dates
      : Array.isArray(campaign.custom_recurring_dates)
        ? campaign.custom_recurring_dates
        : [];

    const allDates = [getCampaignDate(campaign), ...dates].filter(Boolean) as string[];
    const seen = new Set<string>();

    return allDates
      .filter((date) => {
        const key = normalizeDateKey(date);
        if (!key || seen.has(key)) return false;
        seen.add(key);
        return true;
      })
      .sort((a, b) => new Date(a).getTime() - new Date(b).getTime());
  };

  const getRecurringSeriesKey = (campaign: any) =>
    [
      campaign.user || campaign.user_id || "",
      campaign.campaign_type || "",
      campaign.property_id || "",
      campaign.service_id || "",
      campaign.campaign_name || "",
      campaign.campaign_subject || "",
      // ✅ IMPORTANT: Don't include template in the series key
      // This allows template updates without breaking the series grouping
      // campaign.template || "",
      campaign.recurrence_type || "",
      campaign.recurring_end_date || campaign.recurrence_end_date || "",
      JSON.stringify(campaign.custom_recurring_dates || []),
    ].join("|");

  const expandRecurringLaunches = (data: any[]) => {
    const recurringGroups = new Map<string, any[]>();
    const oneTimeCampaigns: any[] = [];

    data.forEach((campaign) => {
      if (!isRecurringCampaign(campaign)) {
        oneTimeCampaigns.push(campaign);
        return;
      }

      const key = getRecurringSeriesKey(campaign);
      recurringGroups.set(key, [...(recurringGroups.get(key) || []), campaign]);
    });

    const expandedRecurring = Array.from(recurringGroups.values()).flatMap((group) => {
      const sortedGroup = [...group].sort(
        (a, b) =>
          new Date(a.created || getCampaignDate(a) || 0).getTime() -
          new Date(b.created || getCampaignDate(b) || 0).getTime()
      );
      const baseCampaign = sortedGroup[0];
      const actualDates = sortedGroup.map((campaign) => getCampaignDate(campaign)).filter(Boolean);
      const baseDates = getRecurringDates(baseCampaign);
      const dates = (
        actualDates.length > 0 && actualDates.length >= baseDates.length
          ? actualDates
          : [...baseDates, ...actualDates]
      )
        .filter((date, index, allDates) =>
          allDates.findIndex((item) => normalizeDateKey(item) === normalizeDateKey(date)) === index
        )
        .sort((a, b) => new Date(a).getTime() - new Date(b).getTime());

      if (dates.length <= 1) return sortedGroup;

      // ✅ DEDUPLICATION: Track which scheduled dates we've already created occurrences for
      const seenScheduledDates = new Set<string>();

      return dates
        .map((scheduledDate, index) => {
          const scheduledKey = normalizeDateKey(scheduledDate);
          
          // ✅ Skip if we've already created an occurrence for this date
          if (seenScheduledDates.has(scheduledKey)) {
            return null;
          }
          seenScheduledDates.add(scheduledKey);
          
          const actualCampaign =
            sortedGroup.find((campaign) => normalizeDateKey(getCampaignDate(campaign)) === scheduledKey) ||
            null;
          const sourceCampaign = actualCampaign || baseCampaign;
          const completedOccurrences =
            Number(baseCampaign.completed_occurrences ?? baseCampaign.timesrun ?? 0) || 0;
          const inferredStatus =
            actualCampaign?.status ||
            (index < completedOccurrences ? "sent" : "scheduled");

          return {
            ...sourceCampaign,
            id: actualCampaign?.id || baseCampaign.id,
            scheduled_date: scheduledDate,
            date: scheduledDate,
            status: inferredStatus,
            is_recurring_occurrence: true,
            is_virtual_occurrence: !actualCampaign,
            recurring_parent_id: baseCampaign.id,
            occurrence_index: index + 1,
            total_occurrences: dates.length,
            total_recurrence_count: dates.length,
            scheduled_dates: dates,
          };
        })
        .filter((item: any) => item !== null);
    });

    return [...oneTimeCampaigns, ...expandedRecurring];
  };

  // ── data fetching ─────────────────────────────────────────────

  const fetchCampaigns = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(endpoints.listCampaigns);
      const data = Array.isArray(response.data)
        ? response.data
        : Array.isArray(response.data?.campaigns)
          ? response.data.campaigns
          : [];

      const expanded = expandRecurringLaunches(data);
      const sorted = [...expanded].sort(
        (a, b) =>
          new Date(getCampaignDate(b) || 0).getTime() -
          new Date(getCampaignDate(a) || 0).getTime()
      );

      setCampaigns(sorted);
      setRecentCampaigns(sorted.slice(0, 5));
    } catch (error) {
      console.error("Error fetching Launches:", error);
      toast.error("Failed to load Launches");
      setCampaigns([]);
      setRecentCampaigns([]);
    } finally {
      setLoading(false);
    }
  };

  // ── stat calculations ─────────────────────────────────────────

  const totalRecipients = campaigns.reduce(
    (sum, c) => sum + (c.recipients_count || c.recipients || 0) * (c.timesrun || 1),
    0
  );

  const totalOpens = campaigns
    .filter((c) => c.status === "sent")
    .reduce((sum, c) => {
      if (typeof c.open_count === "number" && c.open_count > 0) return sum + c.open_count;
      let rate = c.openrate || 0;
      if (rate > 100) rate = rate / 10;
      const recipients = (c.timesrun || 1) * (c.recipients_count || c.recipients || 0);
      return sum + Math.round((rate / 100) * recipients);
    }, 0);

  const totalClicks = campaigns
    .filter((c) => c.status === "sent")
    .reduce((sum, c) => {
      let rate = c.clickrate || 0;
      if (rate > 100) rate = rate / 10;
      const recipients = (c.timesrun || 1) * (c.recipients_count || c.recipients || 0);
      return sum + Math.round((rate / 100) * recipients);
    }, 0);

  const totalEmailsSent = campaigns
    .filter((c) => c.status === "sent")
    .reduce(
      (sum, c) => sum + (c.timesrun || 1) * (c.recipients_count || c.recipients || 0),
      0
    );

  const activeOrSentCampaigns = campaigns.filter(
    (c) => c.status === "sent" || c.status === "running"
  ).length;

  const formatNumber = (n: number) => n.toLocaleString("en-US");

  // ── timezone initialization ───────────────────────────────

  useEffect(() => {
    const initializeTimezone = async () => {
      try {
        // Fetch user profile to get their timezone preference
        const res = await axiosInstance.get(endpoints.me);
        const userData = res.data?.user ?? res.data;

        // Use user's timezone from profile if set, otherwise auto-detect
        if (userData?.timezone) {
          const normalizedUserTimezone = normalizeTimezone(userData.timezone);
          if (normalizedUserTimezone) {
            setTimezone(normalizedUserTimezone);
            return;
          }
        }

        // Auto-detect timezone if not set in user profile
        const autoDetectedTz = normalizeTimezone(detectUserTimezone()) || "UTC";
        setTimezone(autoDetectedTz);
      } catch (err) {
        console.error("Error initializing timezone:", err);
        // Fall back to auto-detection if profile fetch fails
        const autoDetectedTz = normalizeTimezone(detectUserTimezone()) || "UTC";
        setTimezone(autoDetectedTz);
      }
    };

    // Only initialize if timezone is not already set in context
    if (!timezone || timezone === "UTC") {
      initializeTimezone();
    }
  }, []);

  // ── effects ───────────────────────────────────────────────────

  useEffect(() => {
    if (location.state?.updatedCampaign) {
      fetchCampaigns();
      window.history.replaceState({}, "");
    }
  }, [location.state?.updatedCampaign]);

  useEffect(() => {
    fetchCampaigns();
  }, []);

  // ── filter helpers ────────────────────────────────────────────

  const filterByDateRange = (campaign: any) => {
    const campaignDate = getCampaignDate(campaign);
    if (!campaignDate) return false;
    const date = new Date(campaignDate);
    const now = new Date();
    switch (dateRangeFilter) {
      case "today": return isToday(date);
      case "thisWeek": return isWithinInterval(date, { start: startOfWeek(now, { weekStartsOn: 1 }), end: endOfWeek(now, { weekStartsOn: 1 }) });
      case "previousWeek": return isWithinInterval(date, { start: startOfWeek(subWeeks(now, 1), { weekStartsOn: 1 }), end: endOfWeek(subWeeks(now, 1), { weekStartsOn: 1 }) });
      case "thisMonth": return isWithinInterval(date, { start: startOfMonth(now), end: endOfMonth(now) });
      case "lastMonth": return isWithinInterval(date, { start: startOfMonth(subMonths(now, 1)), end: endOfMonth(subMonths(now, 1)) });
      case "thisYear": return date.getFullYear() === now.getFullYear();
      default: return true;
    }
  };

  const getStatusBadge = (status?: string) => {
    switch (status?.toLowerCase()) {
      case "running": return "bg-green-100 text-green-800 border border-green-300";
      case "pending": return "bg-yellow-100 text-yellow-800 border border-yellow-300";
      case "scheduled": return "bg-green-100 text-green-800 border border-green-300";
      case "failed":
      case "error": return "bg-red-100 text-red-800 border border-red-300";
      case "sent": return "bg-blue-100 text-blue-800 border border-blue-300";
      case "cancelled":
      case "canceled": return "bg-gray-100 text-gray-700 border border-gray-300";
      default: return "bg-gray-100 text-gray-800 border border-gray-300";
    }
  };

  // ── computed data ─────────────────────────────────────────────

  const filteredCampaigns = campaigns.filter((campaign) => {
    const matchesSearch =
      (campaign.campaign_name || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
      (campaign.property_title || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
      (campaign.template || "").toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === "all" || campaign.status?.toLowerCase() === statusFilter.toLowerCase();
    const matchesDateRange = dateRangeFilter === "all" || filterByDateRange(campaign);
    return matchesSearch && matchesStatus && matchesDateRange;
  });

  const getCampaignDisplayName = (campaign: any) =>
    campaign.campaign_name?.trim()
      ? campaign.campaign_name.trim()
      : campaign.campaign_type?.toLowerCase() === "service"
        ? campaign.service_type || "Service Launch"
        : campaign.property_title?.trim()
          ? campaign.property_title.trim()
          : "Property Launch";

  const getCampaignTypeLabel = (campaign: any) => {
    const type = campaign?.campaign_type?.toLowerCase();
    if (type === "service") return "Service Launch";
    if (type === "multi_service") return "Multi-service Launch";
    if (type === "multi_property") return "Multi-property Launch";
    return "Property Launch";
  };

  const getCancelCreditMessage = (campaign: any) =>
    campaign?.is_recurring_occurrence || campaign?.is_recurring
      ? "1 scheduled recurring launch credit will be credited in your account."
      : "1 scheduled launch credit will be credited in your account.";

  const getLaunchIqHubCount = (campaign: any) => {
    const associationCount = Array.isArray(campaign?.association_ids) ? campaign.association_ids.length : 0;
    const countyCount = Array.isArray(campaign?.county_ids) ? campaign.county_ids.length : 0;
    return associationCount + countyCount;
  };

  const getExtraHubCount = (campaign: any) => {
    // 1 hub per send is always FREE, only extra hubs beyond that are charged
    const totalHubs = getLaunchIqHubCount(campaign);
    return Math.max(0, totalHubs - 1);
  };

  const getCustomHubCount = (campaign: any) =>
    Array.isArray(campaign?.custom_dataset_names) ? campaign.custom_dataset_names.length : 0;

  const getCancelReturnMessage = (campaign: any) => {
    const totalHubCount = getLaunchIqHubCount(campaign);
    const extraHubCount = getExtraHubCount(campaign);
    
    if (totalHubCount === 0) {
      return getCancelCreditMessage(campaign);
    }
    
    const hubReturnText = extraHubCount > 0 
      ? `1 default hub credit (free) and ${extraHubCount} extra hub credit${extraHubCount === 1 ? "" : "s"}`
      : "1 default hub credit (free, included with every launch)";
    
    return `${getCancelCreditMessage(campaign)} ${hubReturnText} will also be credited in your account.`;
  };

  const closeCancelDialog = () => {
    if (cancelLoading) return;
    setCampaignToCancel(null);
    setCancelStep("review");
  };

  const getCampaignRowKey = (campaign: any) =>
    campaign.is_recurring_occurrence
      ? `${campaign.recurring_parent_id || campaign.id}-${campaign.occurrence_index}-${normalizeDateKey(getCampaignDate(campaign))}`
      : String(campaign.id || campaign.property_id || normalizeDateKey(getCampaignDate(campaign)));

  const navigateToEditCampaign = (campaign: any) => {
    if (isCancelledLaunch(campaign)) {
      toast.error("Cancelled launches cannot be edited");
      return;
    }

    const occurrenceId = campaign.is_recurring_occurrence ? campaign.id : null;
    const parentId = campaign.recurring_parent_id || campaign.id || campaign.property_id;
    const editId = occurrenceId || parentId;
    if (!editId) return;

    if (campaign.is_recurring_occurrence && occurrenceId) {
      // When editing a real recurring occurrence, use that occurrence's own ID
      // so the edit page loads the correct campaign record instead of the parent series.
      navigate(`/launches-edit/${occurrenceId}`);
      return;
    }

    if (campaign.is_recurring_occurrence) {
      // Virtual recurring occurrence: fall back to the parent series with occurrence metadata.
      navigate(`/launches-edit/${parentId}`, {
        state: {
          is_recurring_occurrence: true,
          recurring_parent_id: campaign.recurring_parent_id || campaign.id,
          occurrence_index: campaign.occurrence_index,
          scheduled_date: getCampaignDate(campaign),
        },
      });
      return;
    }

    navigate(`/launches-edit/${editId}`);
  };

  const isScheduledLaunch = (campaign: any) =>
    campaign.status?.toLowerCase() === "scheduled";

  const isCancelledLaunch = (campaign: any) => {
    const status = campaign.status?.toLowerCase();
    return status === "cancelled" || status === "canceled";
  };

  const openCancelConfirm = (campaign: any) => {
    if (!isScheduledLaunch(campaign)) {
      toast.error("Only scheduled launches can be cancelled");
      return;
    }
    setCancelStep("review");
    setCampaignToCancel(campaign);
  };

  const handleCancelCampaign = async () => {
    if (!campaignToCancel) return;
    const id = campaignToCancel.is_virtual_occurrence
      ? (campaignToCancel.recurring_parent_id || campaignToCancel.id)
      : campaignToCancel.id;
    if (!id) {
      toast.error("Unable to cancel this launch");
      return;
    }

    try {
      setCancelLoading(true);
      const response = await axiosInstance.post(endpoints.cancelCampaignById(id), {
        scheduled_date: getCampaignDate(campaignToCancel),
        occurrence_index: campaignToCancel.occurrence_index,
        recurring_parent_id: campaignToCancel.recurring_parent_id,
      });
      toast.success(response.data?.detail || "Launch and hub credits returned");
      setCampaignToCancel(null);
      setCancelStep("review");
      
      // Fetch campaigns to update the list and remove cancelled launch
      await fetchCampaigns();
      
      // Fetch user profile to refresh campaign limits and credits in account
      try {
        await axiosInstance.get(endpoints.me);
        window.dispatchEvent(new Event("user-updated"));
      } catch (userError) {
        console.warn("Failed to refresh user profile after cancel:", userError);
        // Don't fail the whole operation if profile refresh fails
      }
    } catch (error: any) {
      console.error("Error cancelling launch:", error);
      toast.error(error?.response?.data?.detail || "Failed to cancel launch");
    } finally {
      setCancelLoading(false);
    }
  };

  // const isRecurringCampaign = (campaign: any): boolean =>
  //   campaign?.is_recurring === true ||
  //   Boolean(campaign?.recurrence_type);

  // const getRecurrenceInfo = (campaign: any) => {
  //   if (!isRecurringCampaign(campaign)) return null;

  //   return {
  //     type: campaign.recurrence_type || "Unknown",
  //     endDate:
  //       campaign.recurring_end_date ||
  //       campaign.recurrence_end_date ||
  //       null,
  //     totalOccurrences:
  //       campaign.total_recurrence_count ??
  //       campaign.total_occurrences ??
  //       (Array.isArray(campaign.scheduled_dates)
  //         ? campaign.scheduled_dates.length
  //         : null),
  //     completedOccurrences: campaign.completed_occurrences ?? campaign.timesrun ?? 0,
  //     occurrencesRemaining:
  //       campaign.remaining_recurrence_count ??
  //       campaign.remaining_occurrences ??
  //       null,
  //     nextScheduledDate: campaign.next_scheduled_date || null,
  //     scheduledDates: Array.isArray(campaign.scheduled_dates)
  //       ? campaign.scheduled_dates
  //       : Array.isArray(campaign.custom_recurring_dates)
  //         ? campaign.custom_recurring_dates
  //         : [],
  //     customDates: Array.isArray(campaign.custom_recurring_dates)
  //       ? campaign.custom_recurring_dates
  //       : [],
  //   };
  // };

  // const getOccurrenceLabel = (campaign: any) => {
  //   const recurring = getRecurrenceInfo(campaign);
  //   if (!recurring) return "One-time";
  //   const total = recurring.totalOccurrences;
  //   const done = recurring.completedOccurrences ?? 0;
  //   if (total) return `${done}/${total} sent`;
  //   return formatRecurrenceType(recurring.type);
  // };

  // // Helper: Format recurrence type
  // const formatRecurrenceType = (type: string): string => {
  //   switch (type?.toLowerCase()) {
  //     case "daily": return "Daily";
  //     case "weekly": return "Weekly";
  //     case "monthly": return "Monthly";
  //     case "custom": return "Custom";
  //     default: return type || "Unknown";
  //   }
  // };

  // const handleViewLaunchDetails = (campaign: any) => {
  //   setSelectedCampaignDetails(campaign);
  //   setShowDetailsModal(true);
  // };

  const getOpenCount = (campaign: any) => {
    if (typeof campaign.open_count === "number" && campaign.open_count >= 0) return campaign.open_count.toLocaleString();
    let rate = campaign.openrate || 0;
    if (rate > 100) rate = rate / 10;
    const recipients = (campaign.timesrun || 1) * (campaign.recipients_count || campaign.recipients || 0);
    return Math.round((rate / 100) * recipients).toLocaleString();
  };

  const getClickCount = (campaign: any) => {
    if (typeof campaign.click_count === "number" && campaign.click_count >= 0) return campaign.click_count.toLocaleString();
    let rate = campaign.clickrate || 0;
    if (rate > 100) rate = rate / 10;
    const recipients = (campaign.timesrun || 1) * (campaign.recipients_count || campaign.recipients || 0);
    return Math.round((rate / 100) * recipients).toLocaleString();
  };

  // ── stats config ──────────────────────────────────────────────

  const stats = [
    {
      title: "Total Launches",
      value: campaigns.length.toString(),
      icon: Send,
      iconBg: "bg-blue-100",
      iconColor: "text-blue-600",
      barColor: "bg-gradient-to-r from-blue-500 to-blue-600",
    },
    {
      title: "Active / Sent",
      value: activeOrSentCampaigns.toString(),
      icon: TrendingUp,
      iconBg: "bg-emerald-100",
      iconColor: "text-emerald-600",
      barColor: "bg-gradient-to-r from-emerald-500 to-emerald-600",
    },
    {
      title: "Total Recipients",
      value: formatNumber(totalRecipients),
      icon: Users,
      iconBg: "bg-purple-100",
      iconColor: "text-purple-600",
      barColor: "bg-gradient-to-r from-purple-500 to-purple-600",
    },
    {
      title: "Social Media",
      value: "",
      badge: "Coming Soon",
      icon: Share2,
      iconBg: "bg-pink-100",
      iconColor: "text-pink-400",
      barColor: "bg-gradient-to-r from-pink-400 to-pink-500",
      disabled: true,
    },
  ];

  const handleRegisterMobileToggle = useCallback((fn: () => void) => {
    setMobileMenuToggle(() => fn);
  }, []);

  // ── render ────────────────────────────────────────────────────

  return (
    <div className="flex min-h-screen bg-white">
      <Sidebar registerMobileToggle={handleRegisterMobileToggle} />

      <main className="flex-1 flex flex-col min-w-0">
        <PageHeader
          // title="Launch Management"
          // description="Create your marketing launch in simple steps"
          mobileMenuOffset={true}
          onMenuClick={mobileMenuToggle ?? undefined}
        />

        <div className="flex-1 p-3 sm:p-6 lg:p-8 overflow-y-auto">
          <div className="max-w-7xl mx-auto space-y-4 sm:space-y-6">

            {/* ── Stats Grid: 2 cols on mobile, 4 on lg ── */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-6">
              {stats.map((stat, index) => (
                <Card
                  key={index}
                  className={`relative overflow-hidden border-0 shadow-md hover:shadow-lg transition-all duration-300 ${stat.disabled ? "opacity-70" : "hover:-translate-y-0.5"}`}
                >
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-1 pt-3 px-3 sm:px-4 sm:pt-4 sm:pb-2">
                    {/* Title: shorter on mobile */}
                    <CardTitle className="text-xs sm:text-sm font-medium text-gray-600 leading-tight pr-1">
                      {/* Mobile short labels */}
                      <span className="sm:hidden">
                        {index === 0 && "Launches"}
                        {index === 1 && "Active"}
                        {index === 2 && "Recipients"}
                        {index === 3 && "Social"}
                      </span>
                      <span className="hidden sm:inline">{stat.title}</span>
                    </CardTitle>
                    <div className={`p-1.5 sm:p-2.5 rounded-lg sm:rounded-xl flex-shrink-0 ${stat.iconBg}`}>
                      <stat.icon className={`h-3.5 w-3.5 sm:h-5 sm:w-5 ${stat.iconColor}`} />
                    </div>
                  </CardHeader>
                  <CardContent className="px-3 pb-3 sm:px-4 sm:pb-4">
                    {stat.badge ? (
                      <Badge variant="secondary" className="border-blue-300 bg-blue-50 text-blue-700 text-xs">
                        {stat.badge}
                      </Badge>
                    ) : (
                      <div className="text-xl sm:text-3xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent truncate">
                        {stat.value}
                      </div>
                    )}
                  </CardContent>
                  <div className={`absolute bottom-0 left-0 right-0 h-0.5 sm:h-1 ${stat.barColor}`} />
                </Card>
              ))}
            </div>

            {/* ── Filters ── */}
            <Card className="border-0 shadow-md">
              <CardContent className="p-3 sm:p-4 lg:p-6">
                <div className="flex flex-col gap-3">
                  {/* Search — full width always */}
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 h-4 w-4" />
                    <Input
                      placeholder="Search by name, property, or template…"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10 border-gray-200 focus:border-blue-500 focus:ring-blue-500"
                    />
                  </div>
                  {/* Selects — side by side even on mobile */}
                  <div className="flex gap-2 sm:gap-3">
                    <Select value={statusFilter} onValueChange={setStatusFilter}>
                      <SelectTrigger className="flex-1 border-gray-200 text-sm">
                        <SelectValue placeholder="Status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Status</SelectItem>
                        <SelectItem value="Running">Running</SelectItem>
                        <SelectItem value="Scheduled">Scheduled</SelectItem>
                        <SelectItem value="Cancelled">Cancelled</SelectItem>
                        <SelectItem value="Pending">Pending</SelectItem>
                        <SelectItem value="Sent">Sent</SelectItem>
                        <SelectItem value="Failed">Failed</SelectItem>
                      </SelectContent>
                    </Select>
                    <Select value={dateRangeFilter} onValueChange={setDateRangeFilter}>
                      <SelectTrigger className="flex-1 border-gray-200 text-sm">
                        <SelectValue placeholder="Date Range" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Time</SelectItem>
                        <SelectItem value="today">Today</SelectItem>
                        <SelectItem value="thisWeek">This Week</SelectItem>
                        <SelectItem value="previousWeek">Prev Week</SelectItem>
                        <SelectItem value="thisMonth">This Month</SelectItem>
                        <SelectItem value="lastMonth">Last Month</SelectItem>
                        <SelectItem value="thisYear">This Year</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* ── Launches list ── */}
            <Card className="border-0 shadow-lg">
              <CardHeader className="border-b bg-gradient-to-r from-gray-50 to-white px-4 py-3 sm:px-6 sm:py-4">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base sm:text-xl font-bold text-gray-900 flex items-center gap-2">
                    <Send className="h-4 w-4 sm:h-5 sm:w-5 text-blue-600" />
                    All Launches
                  </CardTitle>
                  <Badge variant="outline" className="text-xs sm:text-sm font-medium">
                    {filteredCampaigns.length} launches
                  </Badge>
                </div>
              </CardHeader>

              <CardContent className="p-0">
                {loading ? (
                  <div className="p-6 space-y-4">
                    {[1, 2, 3].map((i) => <Skeleton key={i} className="h-14 w-full" />)}
                  </div>
                ) : campaigns.length === 0 ? (
                  <div className="p-10 text-center">
                    <Send className="h-10 w-10 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500 font-medium">No launches found</p>
                    <p className="text-gray-400 text-sm mt-1">Create your first launch to get started</p>
                  </div>
                ) : filteredCampaigns.length === 0 ? (
                  <div className="p-10 text-center">
                    <Search className="h-10 w-10 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500 font-medium">No launches match your filters</p>
                    <p className="text-gray-400 text-sm mt-1">Try adjusting your search or filters</p>
                  </div>
                ) : (
                  <>
                    {/* ── MOBILE: card list (< md) ── */}
                    <div className="md:hidden divide-y divide-gray-100 max-h-[520px] overflow-y-auto">
                      {filteredCampaigns.map((campaign) => {
                        const isSent = campaign.status?.toLowerCase() === "sent";
                        const isScheduled = isScheduledLaunch(campaign);
                        const isCancelled = isCancelledLaunch(campaign);
                        const isEditDisabled = isSent || isCancelled;
                        const id = campaign.id || campaign.property_id;
                        const isService = campaign.campaign_type?.toLowerCase() === "service";
                        // const recurring = getRecurrenceInfo(campaign);

                        return (
                          <div key={getCampaignRowKey(campaign)} className="p-4 hover:bg-blue-50/40 transition-colors">
                            {/* Row 1: name + actions */}
                            <div className="flex items-start justify-between gap-2 mb-2">
                              <div className="flex-1 min-w-0">
                                <p className="font-semibold text-gray-900 text-sm truncate">
                                  {getCampaignDisplayName(campaign)}
                                </p>
                                <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                                  <Badge
                                    variant="outline"
                                    className={`text-xs px-1.5 py-0 ${isService ? "border-purple-300 bg-purple-50 text-purple-700" : "border-blue-300 bg-blue-50 text-blue-700"}`}
                                  >
                                    {isService ? "Service" : (campaign.campaign_type ? campaign.campaign_type.charAt(0).toUpperCase() + campaign.campaign_type.slice(1).toLowerCase() : "Unknown")}
                                  </Badge>
                                  <Badge className={`${getStatusBadge(campaign.status)} text-xs px-1.5 py-0`}>
                                    {campaign.status ? campaign.status.charAt(0).toUpperCase() + campaign.status.slice(1).toLowerCase() : "Unknown"}
                                  </Badge>
                                  {campaign.is_recurring_occurrence && (
                                    <Badge variant="outline" className="border-orange-300 bg-orange-50 text-orange-700 text-xs px-1.5 py-0">
                                      {campaign.occurrence_index}/{campaign.total_occurrences}
                                    </Badge>
                                  )}
                                </div>
                              </div>
                              {/* Action buttons */}
                              <div className="flex gap-1 flex-shrink-0">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-8 w-8 p-0 hover:bg-blue-100 hover:text-blue-700"
                                  onClick={() => navigate(`/launches-view/${id}`)}
                                  title="View"
                                >
                                  <Eye className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className={`h-8 w-8 p-0 ${isEditDisabled ? "opacity-40 cursor-not-allowed" : "hover:bg-green-100 hover:text-green-700"}`}
                                  onClick={() => !isEditDisabled && navigateToEditCampaign(campaign)}
                                  disabled={isEditDisabled}
                                  title={isCancelled ? "Cancelled launch cannot be edited" : isSent ? "Already sent" : "Edit"}
                                >
                                  <Edit className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className={`h-8 w-8 p-0 ${isScheduled ? "hover:bg-red-100 hover:text-red-700" : "opacity-40 cursor-not-allowed"}`}
                                  onClick={() => isScheduled && openCancelConfirm(campaign)}
                                  disabled={!isScheduled}
                                  title={isScheduled ? "Cancel launch" : "Only scheduled launches can be cancelled"}
                                >
                                  <XCircle className="h-4 w-4" />
                                </Button>
                              </div>
                            </div>

                            {/* Row 1.5: Recurring info (Mobile) */}
                            {/* {recurring && (
                              <button
                                type="button"
                                onClick={() => handleViewLaunchDetails(campaign)}
                                className="w-full text-left text-xs bg-orange-50 border border-orange-200 rounded p-2 mb-2 hover:bg-orange-100"
                              >
                                <div className="font-medium text-orange-700 mb-1">
                                  {formatRecurrenceType(recurring.type)} · {getOccurrenceLabel(campaign)}
                                </div>
                                <div className="text-orange-600 space-y-0.5">
                                  {recurring.totalOccurrences && (
                                    <div>{recurring.totalOccurrences} occurrences total</div>
                                  )}
                                  {recurring.endDate && (
                                    <div>Until {formatDateShort(recurring.endDate)}</div>
                                  )}
                                  {recurring.scheduledDates.length > 0 && (
                                    <div>{recurring.scheduledDates.length} scheduled date(s)</div>
                                  )}
                                </div>
                              </button>
                            )} */}

                            {/* Row 2: meta */}
                            <div className="flex items-center gap-3 text-xs text-gray-500 flex-wrap">
                              <span className="flex items-center gap-1">
                                <Calendar className="h-3 w-3" />
                                {formatDateShort(
                                  campaign.scheduled_date || campaign.date,
                                  campaign.scheduled_timezone || timezone
                                )}
                              </span>
                              <span className="flex items-center gap-1">
                                <Users className="h-3 w-3" />
                                {(campaign.recipients_count || 0).toLocaleString()} recipients
                              </span>
                              {isSent && (
                                <>
                                  <span className="text-gray-400">
                                    👁 {getOpenCount(campaign)} opens
                                  </span>
                                  <span className="text-gray-400">
                                    🖱 {getClickCount(campaign)} clicks
                                  </span>
                                </>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {/* ── DESKTOP: table (md+) ── */}
                    <div className="hidden md:block max-h-[440px] overflow-y-auto border-t">
                      <Table>
                        <TableHeader className="sticky top-0 bg-gray-50 z-20 shadow-sm">
                          <TableRow>
                            <TableHead className="font-semibold text-gray-700">Launch Name</TableHead>
                            <TableHead className="font-semibold text-gray-700">Type</TableHead>
                            {/* <TableHead className="font-semibold text-gray-700">Recurring</TableHead> */}
                            <TableHead className="font-semibold text-gray-700">Date</TableHead>
                            <TableHead className="font-semibold text-gray-700">Recipients</TableHead>
                            <TableHead className="font-semibold text-gray-700">Status</TableHead>
                            <TableHead className="font-semibold text-gray-700">Opens</TableHead>
                            <TableHead className="font-semibold text-gray-700">Clicks</TableHead>
                            <TableHead className="font-semibold text-gray-700 text-right">Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {filteredCampaigns.map((campaign) => {
                            const isSent = campaign.status?.toLowerCase() === "sent";
                            const isScheduled = isScheduledLaunch(campaign);
                            const isCancelled = isCancelledLaunch(campaign);
                            const isEditDisabled = isSent || isCancelled;
                            const id = campaign.id || campaign.property_id;
                            const isService = campaign.campaign_type?.toLowerCase() === "service";
                            // const recurring = getRecurrenceInfo(campaign);

                            return (
                              <TableRow key={getCampaignRowKey(campaign)} className="hover:bg-blue-50/50 transition-colors">
                               <TableCell className="font-medium text-gray-900 w-[140px] max-w-[140px]">
  <div className="truncate w-full" title={getCampaignDisplayName(campaign)}>
                                    {getCampaignDisplayName(campaign)}
                                  </div>
                                  {campaign.is_recurring_occurrence && (
                                    <div className="mt-0.5 text-[11px] text-orange-600 font-medium">
                                      {/* Recurring {campaign.occurrence_index} of {campaign.total_occurrences} */}
                                       Recurring 
                                    </div>
                                  )} 
                                </TableCell>

                                <TableCell>
                                  <Badge
                                    variant="outline"
                                    className={isService
                                      ? "border-purple-300 bg-purple-50 text-purple-700"
                                      : "border-blue-300 bg-blue-50 text-blue-700"
                                    }
                                  >
                                    {isService ? "Service" : (campaign.campaign_type ? campaign.campaign_type.charAt(0).toUpperCase() + campaign.campaign_type.slice(1).toLowerCase() : "Unknown")}
                                  </Badge>
                                </TableCell>

                                {/* <TableCell>
                                  {recurring ? (
                                    <div className="flex flex-col gap-1">
                                      <Badge className="border-orange-300 bg-orange-50 text-orange-700 border w-fit">
                                        {formatRecurrenceType(recurring.type)}
                                      </Badge>
                                      <div className="text-xs text-gray-600">
                                        <div>{getOccurrenceLabel(campaign)}</div>
                                        {recurring.endDate && (
                                          <div className="text-gray-500">
                                            Until {formatDateShort(recurring.endDate)}
                                          </div>
                                        )}
                                        {recurring.scheduledDates.length > 0 && (
                                          <div className="text-gray-500">
                                            {recurring.scheduledDates.length} scheduled date(s)
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  ) : (
                                    <Badge variant="outline" className="border-gray-300 bg-gray-50 text-gray-700">
                                      One-time
                                    </Badge>
                                  )}
                                </TableCell> */}

                                <TableCell className="text-gray-600 text-sm">
                                  {campaign.scheduled_date || campaign.date ? (
                                    <div className="flex flex-col">
                                      <span className="whitespace-nowrap">
                                        {formatDateTimeInTimezone(
                                          campaign.scheduled_date || campaign.date,
                                          campaign.scheduled_timezone || timezone
                                        )}
                                      </span>
                                      <span className="text-xs text-gray-400">
                                        {campaign.scheduled_timezone || timezone || "UTC"}
                                      </span>
                                    </div>
                                  ) : (
                                    <span className="text-gray-400">No date</span>
                                  )}
                                </TableCell>

                                <TableCell>
                                  <div className="flex items-center gap-1.5">
                                    <Users className="h-4 w-4 text-gray-400 flex-shrink-0" />
                                    <span className="font-medium text-gray-900">
                                      {(campaign.recipients_count || 0).toLocaleString()}
                                    </span>
                                  </div>
                                </TableCell>

                                <TableCell>
                                  <Badge className={`${getStatusBadge(campaign.status)} font-medium`}>
                                    {campaign.status ? campaign.status.charAt(0).toUpperCase() + campaign.status.slice(1).toLowerCase() : "Unknown"}
                                  </Badge>
                                </TableCell>

                                <TableCell className="text-gray-900 font-medium">
                                  {isSent ? getOpenCount(campaign) : <span className="text-gray-400">–</span>}
                                </TableCell>

                                <TableCell className="text-gray-900 font-medium">
                                  {isSent ? getClickCount(campaign) : <span className="text-gray-400">–</span>}
                                </TableCell>

                                <TableCell>
                                  <div className="flex justify-end gap-1">
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => navigate(`/launches-view/${id}`)}
                                      className="hover:bg-blue-100 hover:text-blue-700"
                                      title="View launch details"
                                    >
                                      <Eye className="h-4 w-4" />
                                    </Button>
                                    {/* <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => handleViewLaunchDetails(campaign)}
                                      className="hover:bg-orange-100 hover:text-orange-700"
                                      title="View launch details"
                                    >
                                      <Repeat className="h-4 w-4" />
                                    </Button> */}
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => navigateToEditCampaign(campaign)}
                                      disabled={isEditDisabled}
                                      className={isEditDisabled ? "opacity-50 cursor-not-allowed" : "hover:bg-green-100 hover:text-green-700"}
                                      title={isCancelled ? "Cancelled launch cannot be edited" : isSent ? "Launch already sent – editing disabled" : "Edit launch"}
                                    >
                                      <Edit className="h-4 w-4" />
                                    </Button>

                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => isScheduled && openCancelConfirm(campaign)}
                                      disabled={!isScheduled}
                                      className={isScheduled ? "hover:bg-red-100 hover:text-red-700" : "opacity-50 cursor-not-allowed"}
                                      title={isScheduled ? "Cancel launch" : "Only scheduled launches can be cancelled"}
                                    >
                                      <XCircle  className="h-4 w-4" />
                                    </Button>
                                  </div>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>

          </div>
        </div>
        <Dialog open={Boolean(campaignToCancel)} onOpenChange={(open) => !open && closeCancelDialog()}>
          <DialogContent className="w-[95vw] sm:max-w-2xl max-h-[90vh] overflow-y-auto rounded-xl p-0">
            {campaignToCancel && cancelStep === "review" ? (
              <>
                <DialogHeader className="border-b px-6 py-5 text-left">
                  <DialogTitle className="flex items-center gap-2 text-base font-semibold text-emerald-700">
                    <XCircle className="h-5 w-5" />
                    Review Launch Cancellation
                  </DialogTitle>
                  <DialogDescription>
                    Review this scheduled launch before continuing to cancellation.
                  </DialogDescription>
                </DialogHeader>

                <div className="space-y-4 px-6 py-5">
                  <div className="rounded-lg border bg-white">
                    <div className="border-b px-4 py-3">
                      <Badge className="mb-2 bg-blue-50 text-blue-700 hover:bg-blue-50">Cancellation summary</Badge>
                      <h3 className="text-sm font-semibold text-gray-900">{getCampaignDisplayName(campaignToCancel)}</h3>
                      <p className="mt-1 text-xs text-gray-500">
                        {getCampaignTypeLabel(campaignToCancel)} scheduled for{" "}
                        {formatDateTimeInTimezone(
                          getCampaignDate(campaignToCancel),
                          campaignToCancel.scheduled_timezone || timezone
                        )}
                      </p>
                    </div>

                    <div className="space-y-4 p-4">
                      <div>
                        <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-gray-500">
                          Schedule at a glance
                        </p>
                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                          <div className="rounded-lg border bg-gray-50 p-3">
                            <p className="text-[11px] text-gray-500">Launch status</p>
                            <p className="mt-1 text-sm font-semibold text-gray-900">Scheduled</p>
                          </div>
                          <div className="rounded-lg border bg-gray-50 p-3">
                            <p className="text-[11px] text-gray-500">Selected send</p>
                            <p className="mt-1 text-sm font-semibold text-gray-900">
                              {campaignToCancel.is_recurring_occurrence
                                ? `${campaignToCancel.occurrence_index || 1} of ${campaignToCancel.total_occurrences || 1}`
                                : "One-time"}
                            </p>
                          </div>
                          <div className="rounded-lg border bg-gray-50 p-3">
                            <p className="text-[11px] text-gray-500">Timezone</p>
                            <p className="mt-1 text-sm font-semibold text-gray-900">
                              {campaignToCancel.scheduled_timezone || timezone || "UTC"}
                            </p>
                          </div>
                        </div>
                      </div>

                      <div>
                        <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-gray-500">
                          Recipients and hubs
                        </p>
                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                          <div className="rounded-lg border bg-gray-50 p-3">
                            <p className="text-[11px] text-gray-500">Recipients</p>
                            <p className="mt-1 text-sm font-semibold text-gray-900">
                              {(campaignToCancel.recipients_count || campaignToCancel.recipient_count || 0).toLocaleString()}
                            </p>
                          </div>
                          <div className="rounded-lg border bg-gray-50 p-3">
                            <p className="text-[11px] text-gray-500">LaunchIQ Database hubs</p>
                            <p className="mt-1 text-sm font-semibold text-gray-900">
                              {getLaunchIqHubCount(campaignToCancel)}
                            </p>
                          </div>
                          <div className="rounded-lg border bg-gray-50 p-3">
                            <p className="text-[11px] text-gray-500">Your custom hubs</p>
                            <p className="mt-1 text-sm font-semibold text-gray-900">
                              {getCustomHubCount(campaignToCancel)}
                            </p>
                          </div>
                        </div>
                      </div>

                      <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4">
                        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                          <div>
                            <p className="text-sm font-semibold text-emerald-800">Launch credit returns</p>
                            <p className="mt-1 text-xs leading-5 text-emerald-700">
                              {getCancelCreditMessage(campaignToCancel)}
                            </p>
                          </div>
                          <div className="text-left sm:text-right">
                            <p className="text-[11px] font-semibold uppercase text-emerald-700">Credit back</p>
                            <p className="text-2xl font-bold text-emerald-800">1</p>
                          </div>
                        </div>
                      </div>

                      <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4">
                        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                          <div>
                            <p className="text-sm font-semibold text-emerald-800">LaunchIQ Database hub returns</p>
                            <p className="mt-1 text-xs leading-5 text-emerald-700">
                              {getLaunchIqHubCount(campaignToCancel) === 0
                                ? "No LaunchIQ Database hubs selected for this launch."
                                : `1 default hub credit (always free) and ${getExtraHubCount(campaignToCancel)} extra hub credit${getExtraHubCount(campaignToCancel) === 1 ? "" : "s"} (paid) will be credited in your account.`
                              }
                            </p>
                          </div>
                          <div className="text-left sm:text-right">
                            <p className="text-[11px] font-semibold uppercase text-emerald-700">Hub credits back</p>
                            <p className="text-2xl font-bold text-emerald-800">
                              {getExtraHubCount(campaignToCancel)}
                            </p>
                          </div>
                        </div>
                      </div>

                      <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs leading-5 text-amber-800">
                        Note: Continuing will not cancel the launch yet. You can keep the launch on the next screen.
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end gap-3 border-t bg-white px-6 py-4">
                  <Button type="button" variant="outline" onClick={closeCancelDialog}>
                    Back
                  </Button>
                  <Button
                    type="button"
                    className="bg-emerald-600 text-white hover:bg-emerald-700"
                    onClick={() => setCancelStep("confirm")}
                  >
                    Continue
                  </Button>
                </div>
              </>
            ) : (
              <>
                <DialogHeader className="px-6 pt-6 text-left">
                  <DialogTitle>Cancel launch?</DialogTitle>
                  <DialogDescription>
                    This will cancel "{campaignToCancel ? getCampaignDisplayName(campaignToCancel) : "this launch"}". {campaignToCancel ? getCancelReturnMessage(campaignToCancel) : "1 launch credit and selected hub credits will be credited in your account."}
                  </DialogDescription>
                </DialogHeader>
                <div className="flex justify-end gap-3 px-6 pb-6 pt-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={closeCancelDialog}
                    disabled={cancelLoading}
                  >
                    Keep Launch
                  </Button>
                  <Button
                    type="button"
                    className="bg-red-600 text-white hover:bg-red-700"
                    onClick={handleCancelCampaign}
                    disabled={cancelLoading}
                  >
                    {cancelLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Yes, Cancel Launch
                  </Button>
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>
   


        <BotLauncher/>
      </main>
    </div>
  );
};

export default CampaignManagement;
